import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, Button, IconButton, Snackbar, TextField, Tooltip, Typography } from '@mui/material';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { Check, Copy, Plug, Plus, RefreshCw, Search, Wrench } from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';
import mcpServerService from '../../services/mcpServerService';
import { copyTextToClipboard } from '../../utils/clipboard';
import PageLoader from '../shared/PageLoader';
import sharedStyles from '../PlatformAdmin/PlatformAdmin.module.scss';
import styles from './MCPServers.module.scss';
import CreateMcpServerDialog from './CreateMcpServerDialog';
import {
  CREDENTIAL_MODES,
  describeError,
  getCredentialMode,
  getServerStatusMeta,
  getServerTypeMeta,
  isPerUser,
  isPublished,
  unwrapList,
} from './mcpUtils';

/**
 * MCP Servers list (§0 screen 1).
 *
 * Entry point for the builder: every server here is a connector the customer
 * either has published or is still assembling. Cards lead into the detail
 * screen, which owns the import → curate → publish → connect flow.
 */
const MCPServers = () => {
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();

  const [servers, setServers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [copiedId, setCopiedId] = useState(null);
  const [toast, setToast] = useState({ open: false, message: '', severity: 'info' });

  const muiTheme = useMemo(
    () =>
      createTheme({
        palette: {
          mode: isDarkMode ? 'dark' : 'light',
          primary: { main: '#e36250' },
          secondary: { main: '#8b5cf6' },
        },
      }),
    [isDarkMode]
  );

  const notify = useCallback((message, severity = 'info') => {
    setToast({ open: true, message, severity });
  }, []);

  const load = useCallback(async ({ silent = false } = {}) => {
    if (silent) setRefreshing(true);
    else setLoading(true);
    try {
      const data = await mcpServerService.listServers();
      setServers(unwrapList(data, 'servers', 'mcp_servers'));
      setError(null);
    } catch (e) {
      setError(describeError(e, "Couldn't load your MCP servers."));
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return servers;
    return servers.filter((s) =>
      `${s.name || ''} ${s.description || ''} ${s.slug || ''}`.toLowerCase().includes(q)
    );
  }, [servers, search]);

  const handleCopyUrl = async (event, server) => {
    event.stopPropagation();
    try {
      await copyTextToClipboard(server.public_url);
      setCopiedId(server.id);
      setTimeout(() => setCopiedId(null), 2000);
    } catch (e) {
      notify('Copy failed — select the URL and copy it manually.', 'warning');
    }
  };

  const handleCreated = (server) => {
    setCreateOpen(false);
    if (!server?.id) {
      load({ silent: true });
      return;
    }
    // `api` servers start at Import; agent/knowledge have their tools created
    // for them, so they open straight onto the curation list.
    const firstTab = getServerTypeMeta(server.server_type).needsImport ? 'import' : 'tools';
    navigate(`/mcp-servers/${server.id}?tab=${firstTab}`);
  };

  if (loading) {
    return <PageLoader message="Loading MCP servers..." />;
  }

  const renderCard = (server) => {
    const typeMeta = getServerTypeMeta(server.server_type);
    const statusMeta = getServerStatusMeta(server);
    const published = isPublished(server);
    const toolCount = server.enabled_tool_count ?? server.tool_count ?? 0;

    return (
      <div
        key={server.id}
        className={styles.card}
        role="button"
        tabIndex={0}
        onClick={() => navigate(`/mcp-servers/${server.id}`)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            navigate(`/mcp-servers/${server.id}`);
          }
        }}
      >
        <div className={styles.cardTop}>
          <span className={styles.cardIcon}>
            <Plug size={18} />
          </span>
          <span className={`${styles.pill} ${styles[statusMeta.tone]}`}>{statusMeta.label}</span>
        </div>

        <Typography className={styles.cardName}>{server.name || 'Untitled server'}</Typography>
        <Typography className={styles.cardDesc}>
          {server.description || 'No description'}
        </Typography>

        <div className={styles.cardMeta}>
          <span className={styles.tag}>{typeMeta.short}</span>
          {isPerUser(server) && (
            <Tooltip title={CREDENTIAL_MODES[getCredentialMode(server)].helper}>
              <span className={`${styles.tag} ${styles.perUserTag}`}>
                {CREDENTIAL_MODES[getCredentialMode(server)].badge}
              </span>
            </Tooltip>
          )}
          <span className={styles.metaItem}>
            <Wrench size={13} />
            {toolCount} {toolCount === 1 ? 'tool' : 'tools'}
          </span>
          {server.inbound_auth_mode === 'none' && (
            <Tooltip title="This server has no token — anyone with the URL can call it.">
              <span className={`${styles.tag} ${styles.warnTag}`}>no auth</span>
            </Tooltip>
          )}
        </div>

        {/* The list payload carries `slug` but not `public_url` — that only comes
            back from publish/install. So "is it live" is decided by status, and
            the URL is only rendered when the server actually gave us one. */}
        {published && server.public_url ? (
          <div className={styles.urlRow} onClick={(e) => e.stopPropagation()}>
            <code className={styles.url}>{server.public_url}</code>
            <IconButton
              size="small"
              className={sharedStyles.subtleBtn}
              onClick={(e) => handleCopyUrl(e, server)}
              title="Copy public URL"
            >
              {copiedId === server.id ? <Check size={15} color="#10b981" /> : <Copy size={15} />}
            </IconButton>
          </div>
        ) : published ? (
          <div className={styles.urlRow}>
            <code className={styles.url}>{server.slug}</code>
            <span className={sharedStyles.muted}>Open for connection details</span>
          </div>
        ) : (
          <div className={styles.urlRow}>
            <span className={sharedStyles.muted}>Not published yet</span>
          </div>
        )}
      </div>
    );
  };

  return (
    <ThemeProvider theme={muiTheme}>
      <div className={sharedStyles.layout}>
        <div className={sharedStyles.inner}>
          <div className={sharedStyles.header}>
            <div className={sharedStyles.titleSection}>
              <h1>MCP Servers</h1>
              <p>
                Turn an API, an agent, or a knowledge folder into a connector that works in Claude,
                Cursor and the Anthropic/OpenAI APIs.
              </p>
            </div>
            <div className={styles.headerActions}>
              <Button
                className={sharedStyles.outlineBtn}
                variant="outlined"
                startIcon={<RefreshCw size={16} />}
                onClick={() => load({ silent: true })}
                disabled={refreshing}
              >
                Refresh
              </Button>
              <Button
                className={sharedStyles.primaryBtn}
                variant="contained"
                startIcon={<Plus size={16} />}
                onClick={() => setCreateOpen(true)}
              >
                New Server
              </Button>
            </div>
          </div>

          {error && (
            <Alert severity="error" onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {servers.length > 0 && (
            <div className={sharedStyles.toolbar}>
              <TextField
                size="small"
                placeholder="Search servers"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                InputProps={{ startAdornment: <Search size={16} style={{ marginRight: 8 }} /> }}
                sx={{ minWidth: 260 }}
              />
              <span className={sharedStyles.muted}>
                {filtered.length} of {servers.length} shown
              </span>
            </div>
          )}

          {servers.length === 0 ? (
            <div className={sharedStyles.emptyState}>
              <Plug size={44} className={sharedStyles.emptyIcon} />
              <Typography variant="h6">No MCP servers yet</Typography>
              <Typography variant="body2" className={sharedStyles.muted}>
                Turn an API, an agent, or a knowledge folder into a connector your models can call.
              </Typography>
              <Button
                className={sharedStyles.primaryBtn}
                variant="contained"
                startIcon={<Plus size={16} />}
                onClick={() => setCreateOpen(true)}
                sx={{ mt: 2 }}
              >
                Create your first MCP server
              </Button>
            </div>
          ) : filtered.length === 0 ? (
            <div className={sharedStyles.emptyState}>
              <Search size={44} className={sharedStyles.emptyIcon} />
              <Typography variant="h6">No servers match “{search}”</Typography>
              <Button
                className={sharedStyles.outlineBtn}
                variant="outlined"
                onClick={() => setSearch('')}
                sx={{ mt: 2 }}
              >
                Clear search
              </Button>
            </div>
          ) : (
            <div className={styles.grid}>{filtered.map(renderCard)}</div>
          )}
        </div>
      </div>

      <CreateMcpServerDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={handleCreated}
        onError={(message) => notify(message, 'error')}
      />

      <Snackbar
        open={toast.open}
        autoHideDuration={4000}
        onClose={() => setToast((t) => ({ ...t, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setToast((t) => ({ ...t, open: false }))}
          severity={toast.severity}
          sx={{ width: '100%' }}
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </ThemeProvider>
  );
};

export default MCPServers;
